import Link, { type LinkProps } from "next/link"
import {
  Children,
  cloneElement,
  forwardRef,
  type HTMLAttributes,
  isValidElement,
  type ReactNode,
  useRef,
} from "react"
import { useFocusRing, useHover } from "react-aria"
import { twMerge } from "tailwind-merge"
import { Icon } from "../icon"
import type { IconProps } from "../icon/Icon"
import {
  type Color,
  iconSizeLookup,
  makeButtonClassName,
  type Size,
  type Variant,
} from "./Button"

type Override<Type, NewType> = Omit<Type, keyof NewType> & NewType

export type LinkButtonProps = Override<
  HTMLAttributes<HTMLAnchorElement>,
  LinkProps
> & {
  color?: Color
  className?: string
  /** Whether the button is disabled. */
  disabled?: boolean
  size?: Size
  target?: string
  /** The emphasis of the button. `solid` is the most prominent, `ghost` is
   * the least. `muted` should only be used for active button state patterns
   * like tabs. */
  variant?: Variant
  /** The content of the button. Can be a string or an icon. */
  children: ReactNode
  // Necessary for tooltip wrapped LinkButtons to work
  onBlur?: (e: React.FocusEvent<HTMLAnchorElement>) => void
  onFocus?: (e: React.FocusEvent<HTMLAnchorElement>) => void
}

/**
 * A link component styled as a [Button](./Button.tsx) component.
 *
 * ```tsx
 * import { Star } from "@phosphor-icons/react"
 * import { LinkButton } from "@tokenterminal/core/ui/button/LinkButton"
 *
 * <LinkButton href="/">I'm a link</LinkButton>
 * <LinkButton color="success" href="/">
 *   <Icon as={Star} />
 *   I'm also a link
 * </LinkButton>
 * ```
 */

export const LinkButton = forwardRef<HTMLAnchorElement, LinkButtonProps>(
  function LinkButton(
    {
      children,
      className,
      color = "neutral",
      disabled,
      href,
      size = "medium",
      variant = "default",
      ...props
    },
    ref
  ) {
    const fallbackRef = useRef<HTMLAnchorElement>(null)
    const normalizedRef = ref || fallbackRef

    const { focusProps, isFocusVisible, isFocused } = useFocusRing(props)
    const { hoverProps, isHovered } = useHover({})

    return (
      <Link
        ref={normalizedRef}
        href={href}
        className={twMerge(
          makeButtonClassName(size, variant, color),
          className
        )}
        tabIndex={disabled ? -1 : 0}
        data-disabled={disabled || undefined}
        // Wire up aria hooks
        {...props}
        {...focusProps}
        data-focus-visible={isFocusVisible || undefined}
        data-focused={isFocused || undefined}
        {...hoverProps}
        data-hovered={isHovered || undefined}
      >
        {Children.map(children, (child) => {
          if (typeof child === "string") {
            return child
          } else if (isValidElement(child) && child.type === Icon) {
            return cloneElement(child, {
              width: iconSizeLookup[size],
            } as Partial<IconProps>)
          }

          return child
        })}
      </Link>
    )
  }
)
